/**
 * B.H. Copyright (c) 2026 Yemot HaMashiach Ltd.
 * All Rights Reserved.
 *
 * This software is the confidential and proprietary information of
 * Yemot HaMashiach Ltd. ("Confidential Information"). You shall not
 * disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into
 * with Yemot HaMashiach Ltd.
 *
 * Unauthorized copying of this file, via any medium, is strictly prohibited.
 */
import React, {useEffect} from 'react';
import { useLocation, useNavigate } from "react-router-dom";
import {useTranslation } from 'react-i18next';
import {delay} from "../../Helper"

import Frame from '../widgets/Frame';
import RecoveryCodes from '../widgets/RecoveryCodes';

/**
 * מסך הסיום של שחזור סיסמה - ובו גם קודי השחזור החדשים, כשיש כאלה.
 *
 * ## למה הם כאן בכלל
 *
 * `login_salt` הוא גם ה-HMAC-salt של קודי השחזור, והוא מוחלף בכל קביעת סיסמה. כלומר
 * שחזור סיסמה **מבטל את קודי השחזור הקיימים** - הם נשארים בטבלה ואינם ניתנים עוד
 * לאימות. השרת מנפיק מחדש (`TwoFactorRecoveryCode.reissueAfterSaltChange`), והמסך
 * הזה הוא ההזדמנות היחידה לראות אותם: הם נשמרים כתקציר, ואין שירות שמחזיר אותם שוב.
 *
 * ## למה ההפניה האוטומטית נעצרת
 *
 * הפניה אחרי שתי שניות היא הנכונה כשאין מה לקרוא. כאן יש - עשרה סודות חד-פעמיים -
 * ומסך שנעלם מעצמו הוא בדיוק הדרך לאבד אותם. כשיש קודים, המעבר להתחברות נעשה
 * בלחיצה מפורשת שאומרת "שמרתי".
 *
 * לקוח בלי אימות דו-שלבי אינו מקבל קודים (השרת מחזיר רשימה ריקה), והמסך נשאר בדיוק
 * כפי שהיה.
 *
 * ## ההצגה עצמה
 *
 * `RecoveryCodes` - אותו רכיב שמסיים בו את הרישום. הקודים כאן אינם חדשים ללקוח אלא
 * **מחליפים** קודים שהוא כבר שמר, וזה בדיוק מה שהשורה מעליו אומרת; מה הם ומה עושים
 * איתם הוא אותו הסבר בשני המסכים, ואין סיבה שהוא ייכתב פעמיים בשתי גרסאות.
 */
export function PasswordReset() {
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    // הקודים מגיעים במצב הניווט מ-`CreatePassword`. כניסה ישירה לכתובת - אין.
    const recoveryCodes = location.state?.recoveryCodes || [];
    const hasCodes = recoveryCodes.length > 0;

    useEffect(()=>{
        if (hasCodes) {
            return;
        }
        let cancelled = false;
        async function redirect() {
            await delay(2000);
            if (!cancelled) {
                navigate('/login-verify');
            }
        }
        redirect();
        return () => { cancelled = true; };
        // eslint-disable-next-line
    },[]);

    return (
            <Frame>
                <div className='main main-password-reset'>
                    <div className='main-top'>
                        <h1>{t("Password reset successfully")}</h1>
                    </div>
                    <div className='main-body'>
                        <div className='right-main'>
                            <div className='right-main-inner'>
                                {hasCodes ? (
                                    <>
                                        <h2>{t("Your new password has been saved.")}</h2>
                                        <h2>{t("Recovery codes are tied to your password, so the ones you saved before no longer work. These are the codes that replace them.")}</h2>

                                        <RecoveryCodes codes={recoveryCodes} />

                                        <div className='button button-space-between button-teal' onClick={() => navigate('/login-verify')}>
                                            <div className='mr20'>{t("I saved the codes, continue")}</div>
                                            <img src='/images/less-than-white.png' className='ml20 button-icon less-than-icon' alt=''/>
                                        </div>
                                    </>
                                ) : (
                                    <h2>{t("Your new password has been successfully saved. You will be redirected to the login screen within a few seconds.")}</h2>
                                )}
                            </div>
                        </div>    
                        <div className='left-main'>
                            <img src='/images/person-pointing.png' className='content-logo' alt=''/>
                        </div>
                    </div>    
                </div>
            </Frame>
    );
}
